import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACCOUNTANT_ROLES = ['admin', 'senior_accountant'];

const currentMonthDefault = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export const Loans = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [loans, setLoans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterStatus, setFilterStatus] = useState('active');
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    employee_number: '',
    employee_name: '',
    loan_amount: '',
    period_months: '',
    start_month: currentMonthDefault(),
    notes: '',
  });

  const fetchLoans = async () => {
    try {
      const response = await axios.get(`${API}/loans`, { headers: getAuthHeader() });
      setLoans(response.data);
    } catch {
      toast.error(t('noLoansFound'));
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, { headers: getAuthHeader() });
      setEmployees(response.data.sort((a, b) => parseInt(a.employee_number) - parseInt(b.employee_number)));
    } catch {
      toast.error('Failed to fetch employees');
    }
  };

  useEffect(() => {
    fetchLoans();
    fetchEmployees();
  }, []);

  const handleEmployeeSelect = (empNumber) => {
    const employee = employees.find(e => e.employee_number === empNumber);
    if (employee) {
      setFormData(f => ({ ...f, employee_number: empNumber, employee_name: employee.name }));
    }
  };

  const computedInstallment = () => {
    const a = parseFloat(formData.loan_amount);
    const p = parseInt(formData.period_months);
    if (a > 0 && p > 0) return (a / p).toFixed(2);
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_number) { toast.error(t('selectEmployee')); return; }
    const amount = parseFloat(formData.loan_amount);
    const period = parseInt(formData.period_months);
    if (!amount || amount <= 0) { toast.error(t('enterValidAmount')); return; }
    if (!period || period <= 0) { toast.error(t('enterValidQuantity')); return; }
    if (!formData.start_month) { toast.error(t('startMonth')); return; }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/loans`, {
        employee_number: formData.employee_number,
        employee_name: formData.employee_name,
        loan_amount: amount,
        period_months: period,
        start_month: formData.start_month,
        notes: formData.notes,
      }, { headers: getAuthHeader() });
      toast.success(t('loanRecorded'));
      setIsOpen(false);
      fetchLoans();
      setFormData({
        employee_number: '',
        employee_name: '',
        loan_amount: '',
        period_months: '',
        start_month: currentMonthDefault(),
        notes: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('loanRecorded'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const filtered = filterStatus === 'all' ? loans : loans.filter(l => l.status === filterStatus);

  const statusLabel = (s) => s === 'settled' ? t('loanSettled') : t('loanActive');

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('loans')}</h2>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[140px]"
            lang={language === 'ar' ? 'ar' : 'en'}
          >
            <option value="active" lang={language === 'ar' ? 'ar' : 'en'}>{t('loanActive')}</option>
            <option value="settled" lang={language === 'ar' ? 'ar' : 'en'}>{t('loanSettled')}</option>
            <option value="all" lang={language === 'ar' ? 'ar' : 'en'}>{t('loanFilterAll')}</option>
          </select>
        </div>
        {ACCOUNTANT_ROLES.includes(user?.role) && (
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-accent hover:bg-accent/90">
                <Plus className="w-4 h-4" />
                <span lang={language === 'ar' ? 'ar' : 'en'}>{t('newLoan')}</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('recordLoan')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('employee')}</Label>
                  <select
                    value={formData.employee_number}
                    onChange={e => handleEmployeeSelect(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                  >
                    <option value="" lang={language === 'ar' ? 'ar' : 'en'}>{t('selectEmployee')}</option>
                    {employees.map(emp => (
                      <option key={emp.employee_number} value={emp.employee_number}>
                        {emp.employee_number} - {emp.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('loanAmount')} ({t('currency')})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="1"
                    value={formData.loan_amount}
                    onChange={e => setFormData(f => ({ ...f, loan_amount: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('startMonth')}</Label>
                  <Input
                    type="month"
                    value={formData.start_month}
                    onChange={e => setFormData(f => ({ ...f, start_month: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('repaymentPeriod')}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.period_months}
                    onChange={e => setFormData(f => ({ ...f, period_months: e.target.value }))}
                    required
                  />
                  {computedInstallment() && (
                    <p className="text-sm text-muted-foreground mt-1" lang={language === 'ar' ? 'ar' : 'en'}>
                      {t('monthlyInstallment')}: <span className="font-semibold font-mono-numeric" dir="ltr">{computedInstallment()} {t('currency')}</span>
                    </p>
                  )}
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</Label>
                  <Input
                    value={formData.notes}
                    onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                  />
                </div>
                <Button type="submit" className="w-full bg-accent hover:bg-accent/90" disabled={isSubmitting}>
                  <span lang={language === 'ar' ? 'ar' : 'en'}>{t('save')}</span>
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/50">
            <tr>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('number')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('name')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('startMonth')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('loanAmount')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('period')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('monthlyInstallment')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('repaid')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('remaining')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('status')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((loan, idx) => {
              const remaining = Math.max(0, loan.loan_amount - loan.amount_repaid);
              return (
                <tr key={idx} className="hover:bg-muted/30">
                  <td className="border border-border p-3 font-mono-numeric">{loan.employee_number}</td>
                  <td className={`border border-border p-3 ${language === 'ar' ? 'text-base font-bold' : ''}`}>{loan.employee_name}</td>
                  <td className="border border-border p-3 font-mono-numeric" dir="ltr">{loan.start_month}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric" dir="ltr">{loan.loan_amount.toFixed(2)} {t('currency')}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric" dir="ltr">{loan.period_months}m</td>
                  <td className="border border-border p-3 text-end font-mono-numeric" dir="ltr">{loan.monthly_installment.toFixed(2)} {t('currency')}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-success" dir="ltr">{loan.amount_repaid.toFixed(2)} {t('currency')}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-destructive" dir="ltr">{remaining.toFixed(2)} {t('currency')}</td>
                  <td className="border border-border p-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      loan.status === 'settled'
                        ? 'bg-success/20 text-success'
                        : 'bg-warning/20 text-warning'
                    }`} lang={language === 'ar' ? 'ar' : 'en'}>
                      {statusLabel(loan.status)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noLoansFound')}</p>
          </div>
        )}
      </div>
    </Card>
  );
};
